import React, { useEffect, useState } from 'react';
import {
  ELEMENTARY_3D_PRINTING_2026_ID,
  deletePublicTrainingUpload,
  subscribePublicTrainingUploads,
  uploadPublicTrainingScreenshot,
  PublicTrainingUploadDoc,
} from '../../utils/publicTrainingUpload';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  trainingId?: string;
}

const PublicTrainingScreenshotBoard: React.FC<Props> = ({
  trainingId = ELEMENTARY_3D_PRINTING_2026_ID,
}) => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<PublicTrainingUploadDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canManageUploads =
    currentUser?.role === 'superAdmin' ||
    currentUser?.role === 'schoolAdmin' ||
    currentUser?.email === 'jihotech16@gmail.com';

  useEffect(() => {
    const unsub = subscribePublicTrainingUploads(
      trainingId,
      (next) => {
        setItems(next);
        setLoading(false);
        setError(null);
      },
      () => {
        setLoading(false);
        setError('목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      }
    );
    return () => unsub();
  }, [trainingId]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      await uploadPublicTrainingScreenshot(trainingId, file, nickname || undefined);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const copyLink = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('클립보드 복사에 실패했습니다. 주소를 직접 선택해 복사해 주세요.');
    }
  };

  const handleDelete = async (item: PublicTrainingUploadDoc) => {
    const ok = window.confirm(`'${item.originalFileName}' 이미지를 삭제할까요?`);
    if (!ok) return;

    setDeletingId(item.id);
    setError(null);
    try {
      await deletePublicTrainingUpload(item);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--Color-background-normal)',
        borderRadius: '12px',
        border: '1px solid var(--Color-line-normal)',
        padding: '20px',
        marginBottom: '24px',
      }}
    >
      <p
        style={{
          margin: '0 0 16px 0',
          fontSize: '14px',
          color: 'var(--Color-text-normal)',
          lineHeight: 1.6,
        }}
      >
        로그인 없이 스크린샷을 올리면 아래 갤러리에 표시됩니다. 각 이미지의 링크를 복사해
        공유할 수 있습니다.
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px' }}>
          <span style={{ fontSize: '13px', color: 'var(--Color-text-strong)' }}>표시 이름 (선택)</span>
          <input
            type="text"
            value={nickname}
            onChange={(ev) => setNickname(ev.target.value.slice(0, 40))}
            placeholder="예: 김OO"
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--Color-line-normal)',
              fontSize: '14px',
              fontFamily: 'var(--font-family-primary)',
            }}
          />
        </label>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: 'var(--Color-fill-brand-color)',
            color: 'var(--Color-text-inverse)',
            borderRadius: '8px',
            cursor: uploading ? 'wait' : 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            opacity: uploading ? 0.7 : 1,
          }}
        >
          {uploading ? '업로드 중…' : '스크린샷 올리기'}
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={handleFile}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            marginBottom: '12px',
            borderRadius: '8px',
            backgroundColor: 'var(--Color-fill-hover)',
            color: 'var(--Color-text-strong)',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--Color-text-normal)', fontSize: '14px' }}>불러오는 중…</p>
      ) : items.length === 0 ? (
        <p style={{ color: 'var(--Color-text-normal)', fontSize: '14px' }}>
          아직 올라온 이미지가 없습니다. 첫 번째로 공유해 보세요.
        </p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '16px',
          }}
        >
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                border: '1px solid var(--Color-line-normal)',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'var(--Color-background-main)',
              }}
            >
              <a href={item.downloadUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={item.downloadUrl}
                  alt={item.originalFileName}
                  style={{
                    width: '100%',
                    height: '140px',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </a>
              <div style={{ padding: '8px' }}>
                <div
                  style={{
                    fontSize: '12px',
                    color: 'var(--Color-text-normal)',
                    marginBottom: '6px',
                    wordBreak: 'break-all',
                  }}
                >
                  {item.nickname ? `${item.nickname} · ` : ''}
                  {item.originalFileName}
                </div>
                <button
                  type="button"
                  onClick={() => copyLink(item.downloadUrl, item.id)}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: '1px solid var(--Color-line-normal)',
                    backgroundColor: 'var(--Color-background-normal)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-primary)',
                  }}
                >
                  {copiedId === item.id ? '복사됨' : '링크 복사'}
                </button>
                {canManageUploads && (
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    disabled={deletingId === item.id}
                    style={{
                      width: '100%',
                      marginTop: '6px',
                      padding: '6px 8px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      border: '1px solid #e57373',
                      backgroundColor: '#ffebee',
                      color: '#b71c1c',
                      cursor: deletingId === item.id ? 'wait' : 'pointer',
                      fontFamily: 'var(--font-family-primary)',
                    }}
                  >
                    {deletingId === item.id ? '삭제 중…' : '삭제'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicTrainingScreenshotBoard;
